import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

// ============================================
// TYPES
// ============================================

export interface StravaAnalysis {
  id: string;
  userId: string;
  personalityType: string;
  personalityEmoji: string;
  personalityDesc: string;
  totalDistance: number;
  totalActivities: number;
  avgWeeklyRuns: number;
  avgDistance: number;
  longestStreak: number;
  bestMonth: string | null;
  recommendedGoal: number;
  successPrediction: number;
  recommendedTier: string;
  matchReasoning: string | null;
  funFactStat: string | null;
  funFactMessage: string | null;
  motivation: string | null;
  activitiesAnalyzed: number;
  periodStart: string;
  periodEnd: string;
  createdAt: string;
}

export type AnalysisErrorCode =
  | 'NO_STRAVA_CONNECTION'
  | 'TOKEN_EXPIRED'
  | 'ANALYSIS_FAILED'
  | 'RATE_LIMITED';

export interface AnalysisError {
  code: AnalysisErrorCode;
  message: string;
}

// ============================================
// API
// ============================================

export const stravaApi = {
  /**
   * Initiate Strava OAuth (for mobile)
   *
   * @param redirectUrl - URL to redirect back to after OAuth (defaults to /profile-success)
   */
  async getAuthorizationUrl(redirectUrl?: string): Promise<{ authorizationUrl: string }> {
    const params = new URLSearchParams({ mobile: 'true' });
    if (redirectUrl) {
      params.set('redirectUrl', redirectUrl);
    }
    const response = await apiClient.get(`${API_ENDPOINTS.STRAVA.AUTHORIZE}?${params.toString()}`);
    return response.data;
  },

  /**
   * Get Strava connection status
   */
  async getConnectionStatus(): Promise<{
    connected: boolean;
    athlete?: {
      firstname: string;
      lastname: string;
      profileUrl: string;
    };
    scopes?: string;
    connectedAt?: string;
    lastSyncedAt?: string;
  }> {
    const response = await apiClient.get(API_ENDPOINTS.STRAVA.STATUS);
    return response.data;
  },

  /**
   * Disconnect Strava
   */
  async disconnect(): Promise<{ success: boolean }> {
    const response = await apiClient.post(API_ENDPOINTS.STRAVA.DISCONNECT);
    return response.data;
  },

  /**
   * Get existing analysis (returns null if not analyzed yet)
   */
  async getAnalysis(): Promise<{ analysis: StravaAnalysis | null }> {
    const response = await apiClient.get(API_ENDPOINTS.STRAVA.ANALYSIS);
    return response.data;
  },

  /**
   * Trigger new analysis generation
   * May take 5-10 seconds (Claude API call)
   *
   * Returns { analysis: StravaAnalysis } on success
   * Returns { analysis: null, noActivities: true } if user has no running activities
   *
   * @throws Rate limit error (429) with minutesUntilAllowed
   * @throws Analysis error with code and message
   */
  async triggerAnalysis(): Promise<{ analysis: StravaAnalysis | null; noActivities?: boolean }> {
    const response = await apiClient.post(API_ENDPOINTS.STRAVA.ANALYZE);
    return response.data;
  },
};
